import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useFileUpload } from 'components/FirstTimeExperienceModal/steps/BrandProfileDocuments/useFileUpload'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Button } from 'blaze-ui/Button'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { Pdf, TextFile } from 'blaze-ui/icons/24'
import { BrandKitSource, SourceMaterialsMediaObject } from 'types'
import { ACCEPTED_DOCUMENT_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import Styles from '../QuestionnairePanel.module.scss'

interface UploadedDoc {
  id: number
  title: string
  mimeType: string
}

interface Props {
  onAdvance: () => void
  onSkip: () => void
}

function getDocIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <Pdf color="var(--red-50)" size={28} />
  if (mimeType === 'text/plain') return <TextFile color="var(--teal-50)" size={28} />
  return <TextFile color="var(--dark-40)" size={28} />
}

function extractDocsFromSources(sources: BrandKitSource[]): UploadedDoc[] {
  return sources.map((bks) => {
    const src = bks.source as SourceMaterialsMediaObject
    return {
      id: bks.id,
      title: bks.name || 'Document',
      mimeType: src.mime_type || '',
    }
  })
}

export const MarketingDocsStep = ({ onAdvance, onSkip }: Props) => {
  const [docs, setDocs] = useState<UploadedDoc[]>([])
  const { data: brandProfile } = useBrandProfile()
  const brandKitId = brandProfile?.brand_kit?.id

  const { handleUpload, isLoading } = useFileUpload(brandKitId ?? 0, { skipQueryInvalidation: true })

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!brandKitId || !files.length) return
      const dismissToast = showToastMessage('Uploading...', 'loading', undefined, 0)
      try {
        const sources = await handleUpload(files)
        if (sources) {
          setDocs((prev) => [...prev, ...extractDocsFromSources(sources)])
        }
      } finally {
        dismissToast?.()
      }
    },
    [brandKitId, handleUpload],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      uploadFiles(acceptedFiles)
    },
    [uploadFiles],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_DOCUMENT_MIME_TYPES,
    noClick: docs.length > 0,
    noDrag: docs.length > 0,
  })

  const hasDocs = docs.length > 0

  if (hasDocs) {
    return (
      <>
        <div className={Styles.docGrid}>
          {docs.map((doc) => (
            <div key={doc.id} className={Styles.docCard}>
              {getDocIcon(doc.mimeType)}
              <span className={Styles.docCardName}>{doc.title}</span>
            </div>
          ))}
        </div>
        <div className={Styles.actions}>
          <FileTrigger
            onSelect={(fileList) => {
              if (!fileList) return
              uploadFiles(Array.from(fileList))
            }}
            accept={ACCEPTED_DOCUMENT_MIME_TYPES}
            allowsMultiple
          >
            <Button isDisabled={isLoading} size="lg">
              {isLoading ? 'Uploading...' : 'Upload more'}
            </Button>
          </FileTrigger>
          <Button onPress={onAdvance} size="lg" variant="secondary">
            Looks good!
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <div {...getRootProps()} className={`${Styles.dropzone} ${isDragActive ? Styles.dropzoneDragActive : ''}`}>
        <input {...getInputProps()} />
        <span className={Styles.dropzoneText}>
          {isDragActive ? (
            'Drop files here'
          ) : (
            <>
              Drag files here or <span className={Styles.dropzoneBrowse}>browse</span>
            </>
          )}
        </span>
        <span className={Styles.dropzoneHint}>Product docs, marketing descriptions, sample brochures</span>
      </div>
      <div className={Styles.actions}>
        <Button onPress={onSkip} size="lg" variant="secondary">
          Skip
        </Button>
      </div>
    </>
  )
}
